import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';

import { getPageNameLabel } from './myReportsListHelpers';
import { ActionCell, NameCell, ScheduledCell } from './MyReportsRowCells';
import { CELL_SX, NOWRAP_COLUMN_KEYS, ROW_ACTION_REVEAL_CLASS } from './myReportsRowStyles';
import { MyReport } from './MyReportsTable.types';
import { MyReportsColumn } from './MyReportsTableHead';
import { ReportIdCell } from './ReportIdCell';
import { formatFriendlyDateTime } from '../../../utils/common';

export interface MyReportsTableRowProps {
  row: MyReport;
  /** The visible columns to render, in order (already filtered for clients). */
  columns: MyReportsColumn[];
  /**
   * Client/customer users see a curated row: the Shared/Scheduled pills are
   * hidden (their columns are dropped upstream in MyReportsTable).
   */
  isClient: boolean;
  mySiblingId: number | undefined;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** When set, clicking the report name calls this instead of navigating. */
  onSelect?: () => void;
  /** Whether the report is pinned as a tab on its own list page. */
  isPinned?: boolean;
  /** When set, a pin toggle is offered. */
  onTogglePin?: () => void;
  /** When set, a default (star) toggle is offered. */
  onToggleDefault?: () => void;
  even: boolean;
}

export const MyReportsTableRow: React.FC<MyReportsTableRowProps> = ({
  row,
  columns,
  isClient,
  mySiblingId,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onSelect,
  isPinned,
  onTogglePin,
  onToggleDefault,
  even,
}) => {
  const theme = useTheme();
  const pageLabel = getPageNameLabel(row.savedReportType?.name ?? row.pageName ?? '');
  const bg = even ? theme.palette.v4.tableEven : theme.palette.background.paper;
  const isMine = row.reportOwner?.id === mySiblingId;

  const renderCellContent = (key: string): React.ReactNode => {
    switch (key) {
      case 'name':
        return <NameCell row={row} isMine={isMine} isClient={isClient} onSelect={onSelect} />;
      case 'code':
        return (
          <ReportIdCell
            reportId={row.code ?? ''}
            reportType={row.savedReportType?.name ?? row.pageName ?? ''}
          />
        );
      case 'pageName':
        return pageLabel;
      case 'reportOwner':
        return row.reportOwner?.name ?? '';
      case 'createdAt':
        return row.createdAt ? formatFriendlyDateTime(row.createdAt) : '';
      case 'sharedWith':
        return row.sharedWith ?? '';
      case 'scheduledDetails':
        return <ScheduledCell raw={row.scheduledDetails} />;
      default:
        return null;
    }
  };

  return (
    <TableRow
      sx={{
        bgcolor: bg,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: theme.palette.v4.tableHover,
          // The sticky actions cell paints its own background so the columns
          // scrolling under it stay hidden — which also means the row's hover
          // colour stops dead at its edge unless it is repainted here too.
          '& > td:last-of-type': { bgcolor: theme.palette.v4.tableHover },
        },
        // Pin and default are revealed on hover, but stay mounted and focusable
        // so they can still be reached by keyboard (see ROW_ACTION_REVEAL_CLASS).
        [`&:hover .${ROW_ACTION_REVEAL_CLASS}, &:focus-within .${ROW_ACTION_REVEAL_CLASS}`]: {
          opacity: 1,
        },
      }}
    >
      {columns.map((col) => (
        <TableCell
          key={col.key}
          sx={NOWRAP_COLUMN_KEYS.has(col.key) ? { ...CELL_SX, whiteSpace: 'nowrap' } : CELL_SX}
        >
          {renderCellContent(col.key)}
        </TableCell>
      ))}
      <ActionCell
        row={row}
        canEdit={canEdit}
        canDelete={canDelete}
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        onToggleDefault={onToggleDefault}
        onEdit={onEdit}
        onDelete={onDelete}
        bg={bg}
      />
    </TableRow>
  );
};
